import { Badge, BlockStack, Card, Icon, Text } from '@shopify/polaris';
import { AlertCircleIcon, CheckCircleIcon } from '@shopify/polaris-icons';
import type { AuditResult, AuditSeverity } from '../../api/storeAudit';

interface Props {
  results: AuditResult[];
}

function severityTone(severity: AuditSeverity): 'critical' | 'attention' | 'info' {
  switch (severity) {
    case 'critical': return 'critical';
    case 'warning':  return 'attention';
    default:         return 'info';
  }
}

export function AuditResultsList({ results }: Props) {
  const failed = results.filter((r) => !r.passed);
  const passed = results.filter((r) => r.passed);

  return (
    <BlockStack gap="400">
      {failed.length > 0 && (
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Issues to Fix ({failed.length})</Text>
            {failed.map((result) => (
              <AuditResultItem key={result.id} result={result} />
            ))}
          </BlockStack>
        </Card>
      )}

      {passed.length > 0 && (
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">Passing Checks ({passed.length})</Text>
            {passed.map((result) => (
              <AuditResultItem key={result.id} result={result} />
            ))}
          </BlockStack>
        </Card>
      )}
    </BlockStack>
  );
}

function AuditResultItem({ result }: { result: AuditResult }) {
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
      <div style={{ marginTop: '2px', flexShrink: 0 }}>
        <Icon
          source={result.passed ? CheckCircleIcon : AlertCircleIcon}
          tone={result.passed ? 'success' : 'critical'}
          accessibilityLabel={result.passed ? 'Passed' : 'Failed'}
        />
      </div>

      <BlockStack gap="100" inlineAlign="start">
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Text as="span" variant="bodyMd" fontWeight="semibold">{result.title}</Text>
          {result.passed ? (
            <Badge tone="success">Passed</Badge>
          ) : (
            <Badge tone={severityTone(result.severity)}>{result.severity_label}</Badge>
          )}
        </div>

        <Text as="p" variant="bodySm" tone="subdued">{result.description}</Text>

        {!result.passed && result.recommendation && (
          <Text as="p" variant="bodySm" tone="caution">
            Recommendation: {result.recommendation}
          </Text>
        )}
      </BlockStack>
    </div>
  );
}
